import { useConversation } from '../../contexts/ConversationContext';
import { A2UIRenderer } from '../../lib/a2ui-renderer';
import FilterBar from './FilterBar';
import ExportMenu from './ExportMenu';
import SaveViewButton from './SaveViewButton';

export default function Canvas() {
  const { currentHTML, currentA2UI, currentData, submitQuery, isGenerating, activeFilters } = useConversation();

  // Show welcome screen if no visualization
  if (!currentHTML && !currentA2UI) {
    return (
      <div className="h-full flex items-center justify-center p-4 md:p-8">
        <div className="text-left max-w-lg w-full animate-fade-in-up">
          {/* Logo */}
          <div className="w-16 h-16 md:w-20 md:h-20 bg-black flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 md:w-10 md:h-10 text-mint"
              fill="none"
              stroke="currentColor"
              strokeWidth={1}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-2xl md:text-h2 lg:text-h2-lg text-black mb-3 font-light">
            Welcome to LLM Dashboard<span className="text-mint">_</span>
          </h2>

          {/* Subtitle */}
          <p className="text-grey-medium font-light mb-8 text-base md:text-lg">
            Ask me anything about your TES data. Try prompts like:
          </p>

          {/* Quick prompts */}
          <div className="space-y-3">
            {[
              { prompt: 'Show me a chart of things by stage', icon: 'chart' },
              { prompt: 'Create a table of returned items', icon: 'table' },
              { prompt: 'Chart holders by type', icon: 'pie' },
            ].map(({ prompt, icon }) => (
              <button
                key={prompt}
                onClick={() => submitQuery(prompt)}
                disabled={isGenerating}
                className="w-full p-4 bg-white text-left text-sm md:text-base text-black font-light border border-grey-medium/20 hover:border-mint hover:shadow-card active:scale-[0.99] transition-all group disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-grey-light flex items-center justify-center flex-shrink-0 group-hover:bg-mint/10 transition-colors">
                    {icon === 'chart' && (
                      <svg className="w-4 h-4 text-grey-medium group-hover:text-mint transition-colors" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13h4v8H3zm7-5h4v13h-4zm7-5h4v18h-4z" />
                      </svg>
                    )}
                    {icon === 'table' && (
                      <svg className="w-4 h-4 text-grey-medium group-hover:text-mint transition-colors" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                      </svg>
                    )}
                    {icon === 'pie' && (
                      <svg className="w-4 h-4 text-grey-medium group-hover:text-mint transition-colors" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                      </svg>
                    )}
                  </div>
                  <span className="flex-1">"{prompt}"</span>
                  <svg className="w-4 h-4 text-grey-medium group-hover:text-mint group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          {/* Keyboard hint */}
          <p className="mt-6 text-xs text-grey-medium font-mono uppercase tracking-wider">
            Or open the chat panel to start a conversation
          </p>
        </div>
      </div>
    );
  }

  // Prefer A2UI if available, fallback to HTML
  if (currentA2UI) {
    // Get title from first component if it's a chart or table
    const firstTitle = currentA2UI.components?.[0]?.props?.title;
    const vizTitle = typeof firstTitle === 'string' ? firstTitle : 'visualization';

    return (
      <div className="h-full w-full p-3 md:p-4 lg:p-6 overflow-auto bg-grey-light">
        {/* Toolbar: Filters + Save/Export */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 md:mb-4">
          <FilterBar filters={activeFilters} />
          <div className="flex items-center gap-1">
            <SaveViewButton />
            <ExportMenu data={currentData} title={vizTitle} />
          </div>
        </div>

        {/* Main visualization */}
        <div className="animate-fade-in-up">
          <A2UIRenderer
            response={currentA2UI}
            data={currentData}
            className="min-h-full"
          />
        </div>

        {/* Natural Language Insight */}
        {currentA2UI.explanation && (
          <div className="mt-4 md:mt-5 p-3 md:p-4 bg-white border-l-4 border-mint shadow-subtle animate-fade-in-up">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 bg-mint/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-mint" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <p className="text-xs md:text-sm text-black font-light leading-relaxed">
                {currentA2UI.explanation}
              </p>
            </div>
          </div>
        )}

        {/* Suggested Follow-Up Questions */}
        {currentA2UI.suggestedQueries && currentA2UI.suggestedQueries.length > 0 && (
          <div className="mt-4 md:mt-5 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider mb-2">
              Suggested Questions
            </p>
            <div className="flex flex-wrap gap-1.5">
              {currentA2UI.suggestedQueries.map((query, index) => (
                <button
                  key={index}
                  onClick={() => submitQuery(query)}
                  disabled={isGenerating}
                  className="px-2 md:px-3 py-1.5 bg-white text-xs text-black font-light border border-grey-medium/30 hover:border-mint hover:bg-mint/5 hover:shadow-subtle active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {query}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading indicator overlay */}
        {isGenerating && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-toast">
            <div className="bg-black text-white px-4 py-2 shadow-elevated flex items-center gap-2 animate-fade-in-up">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="text-sm font-light">Generating...</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback to legacy HTML iframe
  return (
    <div className="h-full w-full">
      <iframe
        srcDoc={currentHTML!}
        className="w-full h-full border-0"
        sandbox="allow-scripts"
        title="Visualization"
      />
    </div>
  );
}
